import React, { useState, useEffect } from "react";
import { useAuth } from "../../config/AuthContext";
import { makeStyles } from "@material-ui/core/styles";
import {
  Card,
  CardHeader,
  CardActions,
  Button,
  Typography,
  TextField,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
} from "@material-ui/core";
import PostToolbar from "./PostToolbar";
import { categories } from "../../config/categories";
import ReactMarkdown from "react-markdown";
import { renderers } from "../../config/markdown";

const useStyles = makeStyles((theme) => ({
  adminPanel: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    "& .MuiTypography-root": {
      margin: theme.spacing(1, 0),
      color: theme.palette.text.secondary,
    },
  },

  form: {
    width: "100%",
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    "& .MuiTextField-root": {
      margin: theme.spacing(1),
      width: "100%",
    },
    "& .MuiFormControl-root": {
      margin: theme.spacing(1),
      width: "100%",
    },
    "& .MuiButton-root": {
      margin: theme.spacing(1),
      width: "100%",
      background: theme.palette.primary.dark,
    },
  },
  reactMarkdown: {
    overflowY: "scroll",
    overflowWrap: "break-word",
    width: "100%",
    padding: theme.spacing(1),
    minHeight: "90%",
    maxHeight: "100%",
    border: `1px solid ${theme.palette.secondary.main}`,
    borderRadius: "0.25rem",
    display: "flex",
    flexDirection: "column",
    //justifyContent: "center",
    alignItems: "center",
    "& img": {
      maxWidth: "100%",
      padding: theme.spacing(1),
    },
    "& pre": {
      background: "#000",
      borderRadius: "0.25rem",
      width: "inherit",
      padding: theme.spacing(1),
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      "& code": {
        margin: theme.spacing(1),
        color: theme.palette.primary.contrastText,
      },
    },
  },
  publishedBlogsGrid: {
    padding: theme.spacing(5),
  },
  publishedBlogCard: {
    border: `2px solid ${theme.palette.primary.dark}`,
    boxShadow: `7px 7px 2px ${theme.palette.primary.shadow}`,
    "& .MuiButton-root": {
      color: theme.palette.text.primary,
      fontWeight: "600",
    },
  },
}));

const Admin = () => {
  const { dbRef, currentUser } = useAuth();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [post, setPost] = useState("");
  const [blogKey, setBlogKey] = useState("");
  const classes = useStyles();
  const [blogList, setBlogList] = useState([]);

  const [error, setError] = useState({
    titleError: false,
    titleErrorHelperText: "",
    categoryError: false,
    categoryErrorHelperText: "",
    postError: false,
    postErrorHelperText: "",
  });
  let blogListArr = [];
  let blogsContent = [];

  const validateForm = () => {
    if (!title) {
      setError({
        titleError: true,
        titleErrorHelperText: "Title cannot be empty!",
        categoryError: false,
        categoryErrorHelperText: "",
        postError: false,
        postErrorHelperText: "",
      });
      return false;
    }
    if (!category) {
      setError({
        titleError: false,
        titleErrorHelperText: "",
        categoryError: true,
        categoryErrorHelperText: "Category cannot be empty!",
        postError: false,
        postErrorHelperText: "",
      });
      return false;
    }
    if (!post) {
      setError({
        titleError: false,
        titleErrorHelperText: "",
        categoryError: false,
        categoryErrorHelperText: "",
        postError: true,
        postErrorHelperText: "Post cannot be empty!",
      });
      return false;
    }
    return true;
  };
  const handlePostSubmit = (e) => {
    e.preventDefault();
    const valid = validateForm();

    if (valid && blogKey) {
      dbRef.child(`blogs/${blogKey}`).update({
        BlogTitle: `${title.slice(0, 1).toUpperCase()}${title.slice(1)}`,
        BlogCategory: category,
        BlogPost: post,
        BlogDate: new Date().toLocaleString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }),
      });
      setTitle("");
      setCategory("");
      setPost("");
      setBlogKey("");
      setError({
        titleError: false,
        titleErrorHelperText: "",
        categoryError: false,
        categoryErrorHelperText: "",
        postError: false,
        postErrorHelperText: "",
      });
    }

    if (valid && !blogKey) {
      dbRef
        .child(`blogs`)
        .push({
          BlogTitle: `${title.slice(0, 1).toUpperCase()}${title.slice(1)}`,
          BlogCategory: category,
          BlogPost: post,
          BlogDate: new Date().toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          }),
          BlogAppreciate: 0,
          BlogViews: 0,
          BlogAuthor: currentUser.displayName,
        })
        .then(async (data) => {
          await dbRef
            .child("users")
            .child(currentUser.uid)
            .child("blogs")
            .push(data.key);
        });

      setTitle("");
      setCategory("");
      setPost("");
      setError({
        titleError: false,
        titleErrorHelperText: "",
        categoryError: false,
        categoryErrorHelperText: "",
        postError: false,
        postErrorHelperText: "",
      });
    }
  };

  const handleBlogEdit = (blogKey, blogTitle, blogCategory, blogPost) => {
    setTitle(blogTitle);
    setCategory(blogCategory);
    setPost(blogPost);
    setBlogKey(blogKey);
  };

  const handleBlogDelete = (blogKey) => {
    dbRef
      .child(`blogs/${blogKey}`)
      .remove()
      .then(() => {
        dbRef.child(`users/${currentUser.uid}/blogs`).once("value", (snap) => {
          snap.forEach((child) => {
            if (child.val() === blogKey) {
              dbRef
                .child(`users/${currentUser.uid}/blogs`)
                .child(child.key)
                .remove();
            }
          });
        });
      });
  };

  useEffect(() => {
    const unsubscribe = dbRef
      .child(`users/${currentUser.uid}/blogs`)
      .on("value", (snap) => {
        setBlogList(snap);
      });
    return unsubscribe;
  }, []);

  blogList.forEach((child) => {
    blogListArr.push(child.val());
  });

  blogListArr.map((blogItem) => {
    dbRef
      .child("blogs")
      .child(blogItem)
      .on("value", (snap) => {
        blogsContent.push(snap);
      });
  });

  const blogsCollection = blogsContent.map((blog, index) => {
    const blogKey = blog.key;
    const blogTitle = blog.val().BlogTitle;
    const blogCategory = blog.val().BlogCategory;
    const blogPost = blog.val().BlogPost;
    const blogDate = blog.val().BlogDate;
    return (
      <Grid item xs={12} sm={6} md={4} xl={3} key={index}>
        <Card className={classes.publishedBlogCard}>
          <CardHeader title={blogTitle} subheader={blogDate} />
          <CardActions>
            <Button
              size="small"
              color="primary"
              onClick={() =>
                handleBlogEdit(blogKey, blogTitle, blogCategory, blogPost)
              }
            >
              Edit
            </Button>
            <Button
              size="small"
              color="primary"
              onClick={() => handleBlogDelete(blogKey)}
            >
              Delete
            </Button>
          </CardActions>
        </Card>
      </Grid>
    );
  });

  return (
    <div className={classes.adminPanel}>
      <Typography variant="body1">
        Welcome, {currentUser.displayName}.
      </Typography>

      <Grid container spacing={1}>
        <Grid item xs={12} md={6}>
          <Typography variant="body1">Write your blog post below.</Typography>
          <form className={classes.form} autoComplete="off">
            <TextField
              color="secondary"
              label="Title"
              onChange={(e) => setTitle(e.target.value)}
              value={title}
              error={error.titleError}
              helperText={error.titleErrorHelperText}
              variant="outlined"
            />

            <FormControl
              variant="outlined"
              className={classes.formControl}
              error={error.categoryError}
            >
              <InputLabel id="demo-simple-select-outlined-label">
                Category
              </InputLabel>
              <Select
                color="secondary"
                labelId="demo-simple-select-outlined-label"
                id="demo-simple-select-outlined"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                label="Category"
              >
                {categories.map((category, index) => {
                  return (
                    <MenuItem value={category.title} key={index}>
                      {category.title}
                    </MenuItem>
                  );
                })}
              </Select>
              <FormHelperText>{error.categoryErrorHelperText}</FormHelperText>
            </FormControl>

            <TextField
              color="secondary"
              label="Post"
              rows={10}
              multiline
              variant="outlined"
              onChange={(e) => setPost(e.target.value)}
              value={post}
              error={error.postError}
              helperText={error.postErrorHelperText}
            />

            <PostToolbar setPost={setPost} />
            <Button
              color="primary"
              type="submit"
              onClick={(e) => handlePostSubmit(e)}
              variant="contained"
            >
              PUBLISH
            </Button>
          </form>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="body1">Post preview</Typography>
          <ReactMarkdown
            children={post}
            renderers={renderers}
            className={classes.reactMarkdown}
          />
        </Grid>
      </Grid>

      <Typography variant="h5" align="center">
        Published Blogs Section
      </Typography>
      <Grid container spacing={3} className={classes.publishedBlogsGrid}>
        {blogsCollection}
      </Grid>
    </div>
  );
};

export default Admin;
